import { io, Socket } from 'socket.io-client'
import type { Message } from '../types'

const SOCKET_URL = import.meta.env.VITE_WS_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000')

// Lit sessionStorage (comme client.ts) — session non persistée après fermeture du navigateur
function getStoredToken(): string | null {
  try {
    const raw = sessionStorage.getItem('auth-storage')
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state?: { token?: string } }
    return parsed?.state?.token ?? null
  } catch { return null }
}

class WebSocketClient {
  private socket: Socket | null = null
  private listeners: Map<string, Set<(data: unknown) => void>> = new Map()

  connect(userId: string): void {
    const token = getStoredToken()

    if (this.socket?.connected) {
      return
    }

    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }

    this.socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 1000,
    })

    this.socket.on('connect', () => {
      console.log('WebSocket connected')
      this.socket?.emit('user:connect', { userId })
    })

    this.socket.on('disconnect', () => {
      console.log('WebSocket disconnected')
    })

    this.socket.on('user:connected', () => {
      console.log('User connected to WebSocket')
    })

    this.socket.on('message:new', (data: Message) => {
      this.emit('message:new', data)
    })

    this.socket.on('message:updated', (data: Message) => {
      this.emit('message:updated', data)
    })

    this.socket.on('appointment:reminder', (data: unknown) => {
      this.emit('appointment:reminder', data)
    })
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }
  }

  sendMessage(data: {
    content: string
    senderId: string
    receiverId: string
    patientId?: number
  }): void {
    if (this.socket?.connected) {
      this.socket.emit('message:send', data)
    }
  }

  joinConversation(conversationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('conversation:join', conversationId)
    }
  }

  leaveConversation(conversationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('conversation:leave', conversationId)
    }
  }

  markAsRead(messageIds: number[], userId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('message:read', { messageIds, userId })
    }
  }

  on(event: string, callback: (data: unknown) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)?.add(callback)
  }

  off(event: string, callback: (data: unknown) => void): void {
    this.listeners.get(event)?.delete(callback)
  }

  private emit(event: string, data: unknown): void {
    this.listeners.get(event)?.forEach(callback => {
      try {
        callback(data)
      } catch (error) {
        console.error(`Error in WebSocket listener for ${event}:`, error)
      }
    })
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false
  }
}

export const websocketClient = new WebSocketClient()
